import * as React from 'react'
import { useNavigate } from '@tanstack/react-router'
import { User, FileText, Phone, Mail, MapPin, Calendar, MessageCircle, Maximize2 } from 'lucide-react'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { WhatsAppButton } from '@/components/whatsapp'
import { usePatient } from '@/hooks/usePatients'
import { formatCurrency, maskNRIC } from '@/lib/utils'
import { isValidWhatsAppNumber } from '@/lib/whatsapp'

interface PatientSheetProps {
  patientId: number | null
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function PatientSheet({
  patientId,
  open,
  onOpenChange,
}: PatientSheetProps) {
  const navigate = useNavigate()
  const { data, isLoading } = usePatient(patientId)

  const patient = data?.data
  const stats = data?.stats
  const recentInvoices = data?.recentInvoices

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-xl overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <User className="h-5 w-5" />
            {isLoading ? (
              <Skeleton className="h-6 w-48" />
            ) : (
              patient?.name || 'Patient Details'
            )}
          </SheetTitle>
          <SheetDescription>
            {isLoading ? (
              <Skeleton className="h-4 w-32" />
            ) : (
              <>
                PNO: {patient?.pno} · {patient?.branchName}
              </>
            )}
          </SheetDescription>
        </SheetHeader>

        {/* View Full Profile */}
        {patientId && (
          <Button
            variant="outline"
            size="sm"
            className="mt-3 w-full"
            onClick={() => {
              onOpenChange(false)
              navigate({ to: '/patients/$id', params: { id: String(patientId) } })
            }}
          >
            <Maximize2 className="w-4 h-4 mr-2" />
            View Full Profile
          </Button>
        )}

        {isLoading ? (
          <div className="space-y-6 mt-6">
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-24 w-full" />
          </div>
        ) : patient ? (
          <div className="space-y-6 mt-6">
            {/* Stats Cards */}
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-lg bg-muted p-4">
                <div className="text-2xl font-bold">{stats?.totalInvoices ?? 0}</div>
                <div className="text-xs text-muted-foreground">Total Visits</div>
              </div>
              <div className="rounded-lg bg-muted p-4">
                <div className="text-2xl font-bold">
                  {formatCurrency(stats?.totalRevenue ?? 0)}
                </div>
                <div className="text-xs text-muted-foreground">Total Spent</div>
              </div>
            </div>

            {/* WhatsApp Quick Actions */}
            {isValidWhatsAppNumber(patient.phone) && (
              <div className="rounded-xl border border-green-500/20 bg-green-500/5 p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-green-500/10">
                      <MessageCircle className="h-5 w-5" style={{ color: '#25D366' }} fill="#25D366" />
                    </div>
                    <div>
                      <div className="font-medium text-sm">Send WhatsApp</div>
                      <div className="text-xs text-muted-foreground">
                        {patient.phone}
                      </div>
                    </div>
                  </div>
                  <WhatsAppButton
                    phone={patient.phone}
                    patientName={patient.name}
                    branchName={patient.branchName}
                  />
                </div>
              </div>
            )}

            {/* Patient Info */}
            <div className="space-y-4">
              <h3 className="text-sm font-medium">Personal Information</h3>
              <div className="space-y-3">
                <InfoRow icon={User} label="Name" value={patient.name} />
                <InfoRow
                  icon={FileText}
                  label="NRIC"
                  value={patient.nric ? maskNRIC(patient.nric) : '-'}
                />
                <InfoRow icon={Phone} label="Phone" value={patient.phone || '-'} />
                <InfoRow icon={Mail} label="Email" value={patient.email || '-'} />
                <InfoRow
                  icon={User}
                  label="Gender"
                  value={patient.gender || '-'}
                />
                <InfoRow icon={MapPin} label="Address" value={patient.address || '-'} />
                <InfoRow
                  icon={Calendar}
                  label="First Visit"
                  value={stats?.firstVisit ? new Date(stats.firstVisit).toLocaleDateString('en-MY') : '-'}
                />
                <InfoRow
                  icon={Calendar}
                  label="Last Visit"
                  value={stats?.lastVisit ? new Date(stats.lastVisit).toLocaleDateString('en-MY') : '-'}
                />
              </div>
            </div>

            {/* Recent Invoices */}
            {recentInvoices && recentInvoices.length > 0 && (
              <div className="space-y-3">
                <h3 className="text-sm font-medium">Recent Invoices</h3>
                <div className="space-y-2">
                  {recentInvoices.map((invoice) => (
                    <div
                      key={invoice.id}
                      className="flex items-center justify-between rounded-lg border p-3"
                    >
                      <div>
                        <div className="font-medium text-sm">{invoice.invoiceNo}</div>
                        <div className="text-xs text-muted-foreground">
                          {new Date(invoice.invoiceDate).toLocaleDateString('en-MY')}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-medium">
                          {formatCurrency(parseFloat(invoice.grandTotal))}
                        </div>
                        {invoice.patientType && (
                          <Badge
                            variant={invoice.patientType === 'New' ? 'info' : 'secondary'}
                            className="text-xs"
                          >
                            {invoice.patientType}
                          </Badge>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="flex items-center justify-center h-64 text-muted-foreground">
            Patient not found
          </div>
        )}
      </SheetContent>
    </Sheet>
  )
}

function InfoRow({
  icon: Icon,
  label,
  value,
}: {
  icon: React.ComponentType<{ className?: string }>
  label: string
  value: string
}) {
  return (
    <div className="flex items-start gap-3">
      <Icon className="h-4 w-4 mt-0.5 text-muted-foreground" />
      <div className="flex-1">
        <div className="text-xs text-muted-foreground">{label}</div>
        <div className="text-sm">{value}</div>
      </div>
    </div>
  )
}
